<template>
  <div class="order-management">
    <h2>商城订单管理</h2>

    <!-- 统计卡片 -->
    <el-row :gutter="16" style="margin-bottom:16px">
      <el-col :span="6">
        <el-card shadow="hover"><div class="stat-card"><span class="stat-num">{{ stats.todayOrders || 0 }}</span><span class="stat-label">今日支付订单</span></div></el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover"><div class="stat-card"><span class="stat-num">{{ stats.totalOrders || 0 }}</span><span class="stat-label">累计有效订单</span></div></el-card>
      </el-col>
    </el-row>

    <el-card class="refund-panel" shadow="never">
      <template #header>
        <div class="refund-header">
          <strong>退款审核与跟踪</strong>
          <div>
            <el-select v-model="refundStatus" clearable placeholder="全部状态" style="width:150px" @change="handleRefundFilter">
              <el-option label="待审核/可重试" value="REVIEWABLE" />
              <el-option label="微信处理中" value="IN_PROGRESS" />
              <el-option label="异常待处理" value="EXCEPTION" />
              <el-option label="退款成功" value="SUCCESS" />
            </el-select>
            <el-button style="margin-left:8px" @click="loadRefunds">刷新</el-button>
          </div>
        </div>
      </template>
      <el-alert
        v-if="refundList.some(item => item.status === 'ABNORMAL')"
        title="存在退款异常单：请前往微信商户平台 → 交易中心处理，处理后点击“同步微信状态”。"
        type="error" :closable="false" show-icon style="margin-bottom:12px" />
      <el-table :data="refundList" border stripe v-loading="refundLoading" empty-text="暂无退款记录">
        <el-table-column prop="orderNo" label="订单号" width="170" />
        <el-table-column prop="userId" label="用户ID" width="90" />
        <el-table-column label="类型" width="90">
          <template #default="{ row }">{{ row.refundType === 'RETURN' ? '退货退款' : '仅退款' }}</template>
        </el-table-column>
        <el-table-column label="金额" width="100">
          <template #default="{ row }">¥{{ row.refundAmount }}</template>
        </el-table-column>
        <el-table-column prop="refundReason" label="原因" min-width="150" show-overflow-tooltip />
        <el-table-column label="状态" width="120">
          <template #default="{ row }">
            <el-tag :type="refundStatusTag(row.status)">{{ refundStatusText(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="outRefundNo" label="商户退款单号" min-width="210" show-overflow-tooltip />
        <el-table-column prop="lastErrorMessage" label="最近信息" min-width="180" show-overflow-tooltip />
        <el-table-column label="申请时间" width="165">
          <template #default="{ row }">{{ row.createdTime || '-' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template #default="{ row }">
            <el-button v-if="canApproveRefund(row)" size="small" type="primary" @click="handleApproveRefund(row)">
              {{ row.status === 'CLOSED' ? '重新退款' : '通过' }}
            </el-button>
            <el-button v-if="canRejectRefund(row)" size="small" type="danger" @click="handleRejectRefund(row)">拒绝/关闭</el-button>
            <el-button v-if="canSyncRefund(row)" size="small" @click="handleSyncRefund(row)">同步微信状态</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination">
        <el-pagination background @current-change="handleRefundPageChange"
          :current-page="refundPage" :page-size="refundSize" :total="refundTotal"
          layout="total, prev, pager, next" />
      </div>
    </el-card>

    <!-- 搜索栏 -->
    <el-form :inline="true" :model="searchForm" class="search-bar">
      <el-form-item label="订单号">
        <el-input v-model="searchForm.orderNo" placeholder="输入订单号" clearable />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.orderStatus" placeholder="全部" clearable style="width:100px">
          <el-option label="待支付" :value="0" />
          <el-option label="已支付" :value="1" />
          <el-option label="已发货" :value="2" />
          <el-option label="已完成" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="loadOrders">查询</el-button>
      </el-form-item>
    </el-form>

    <!-- 订单表格 -->
    <el-table :data="orderList" border stripe v-loading="loading" style="width:100%">
      <el-table-column label="下单时间" width="160">
        <template #default="{ row }">{{ row.createdTime }}</template>
      </el-table-column>
      <el-table-column prop="orderNo" label="订单号" width="160" />
      <el-table-column prop="userId" label="用户ID" width="80" />
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.orderStatus)">{{ statusText(row.orderStatus) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="totalAmount" label="总价" width="80" />
      <el-table-column prop="payableAmount" label="实付" width="80" />
      <el-table-column label="运费" width="70">
        <template #default="{ row }">{{ freightText(row.freightType) }}</template>
      </el-table-column>
      <el-table-column prop="receiverName" label="收货人" width="100" />
      <el-table-column prop="receiverPhone" label="收货人电话" width="120" />
      <el-table-column label="地址" min-width="160">
        <template #default="{ row }">{{ row.receiverAddress }}</template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button size="small" @click="showDetail(row)">详情</el-button>
          <el-button v-if="row.orderStatus === 1" size="small" type="success" @click="openCreateShipment(row)">发货</el-button>
          <el-button v-if="row.orderStatus === 1 || row.orderStatus === 2 || row.orderStatus === 3" size="small" type="warning" @click="openInitiateRefund(row)">退款</el-button>
        </template>
      </el-table-column>
    </el-table>

   <div class="pagination">
     <el-pagination background @size-change="handleSizeChange" @current-change="handlePageChange"
       :current-page="page" :page-size="size" :total="total"
       :page-sizes="[20, 50, 100]" layout="total, sizes, prev, pager, next, jumper" />
   </div>

    <!-- 订单详情弹窗 -->
    <el-dialog title="订单详情" v-model="detailVisible" width="900px">
      <el-tabs v-model="activeTab">
        <!-- 基本信息 -->
        <el-tab-pane label="基本信息" name="basic">
          <div v-if="detail">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="订单号">{{ detail.orderNo }}</el-descriptions-item>
              <el-descriptions-item label="状态">{{ statusText(detail.orderStatus) }}</el-descriptions-item>
              <el-descriptions-item label="总价">¥{{ detail.totalAmount }}</el-descriptions-item>
              <el-descriptions-item label="实付">¥{{ detail.payableAmount }}</el-descriptions-item>
              <el-descriptions-item label="运费">{{ freightText(detail.freightType) }}</el-descriptions-item>
              <el-descriptions-item label="收货人">{{ detail.receiverName }}</el-descriptions-item>
              <el-descriptions-item label="电话">{{ detail.receiverPhone }}</el-descriptions-item>
              <el-descriptions-item label="地址" :span="2">{{ detail.receiverAddress }}</el-descriptions-item>
            </el-descriptions>
            <h4 style="margin-top:16px">商品明细</h4>
            <el-table :data="detail.items" border size="small">
              <el-table-column prop="productName" label="商品" />
              <el-table-column prop="quantity" label="数量" width="60" />
              <el-table-column prop="unitPrice" label="单价" width="80" />
              <el-table-column prop="subtotalAmount" label="小计" width="80" />
            </el-table>
          </div>
        </el-tab-pane>

        <!-- 发货记录 -->
        <el-tab-pane label="发货记录" name="shipments">
          <div class="shipment-section">
            <div v-if="shipments.length === 0" class="empty-hint">暂无发货记录</div>
            <div v-for="shipment in shipments" :key="shipment.id" class="shipment-card">
              <div class="shipment-header">
                <span>发货单号：{{ shipment.shipmentNo }}</span>
                <el-tag :type="shipmentStatusTag(shipment.shipmentStatus)">
                  {{ shipment.shipmentStatusText || shipmentStatusText(shipment.shipmentStatus) }}
                </el-tag>
              </div>
              <div class="shipment-body">
                <p><strong>运单号：</strong>{{ shipment.trackNo }}</p>
                <p><strong>发货时间：</strong>{{ shipment.shippedTime || '-' }}</p>
                <p><strong>操作人：</strong>{{ shipment.operatorName || '-' }}</p>
                <p v-if="shipment.remark"><strong>备注：</strong>{{ shipment.remark }}</p>
              </div>
              <div class="shipment-items" v-if="shipment.items && shipment.items.length">
                <h4>本次发货商品：</h4>
                <el-table :data="shipment.items" size="small">
                  <el-table-column prop="productName" label="商品" />
                  <el-table-column prop="quantity" label="数量" width="80" />
                </el-table>
              </div>
              <div class="shipment-actions">
                <el-button size="small" @click="openEditShipment(shipment)">修改运单号</el-button>
                <el-button size="small" type="danger"
                           v-if="shipment.shipmentStatus === 0"
                           @click="deleteShipment(shipment.id)">删除</el-button>
              </div>
            </div>
            <el-button type="primary" @click="openCreateShipment(detail)" v-if="detail && detail.orderStatus === 1">
              新增发货
            </el-button>
          </div>
        </el-tab-pane>

        <!-- 变更历史 -->
        <el-tab-pane label="变更历史" name="history">
          <el-timeline v-if="logisticsLogs.length">
            <el-timeline-item v-for="log in logisticsLogs" :key="log.id" :timestamp="log.createdTime">
              <div>
                <p>操作类型：{{ changeTypeText(log.changeType) }}</p>
                <p v-if="log.oldTrackNo">原运单号：{{ log.oldTrackNo }}</p>
                <p v-if="log.newTrackNo">新运单号：{{ log.newTrackNo }}</p>
                <p>操作人：{{ log.operatorName || '-' }}</p>
                <p v-if="log.reason">原因：{{ log.reason }}</p>
              </div>
            </el-timeline-item>
          </el-timeline>
          <div v-else class="empty-hint">暂无变更记录</div>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <!-- 新增发货弹窗 -->
    <el-dialog title="新增发货" v-model="createShipmentVisible" width="500px">
      <el-form label-width="100px">
        <el-form-item label="运单号" required>
          <el-input v-model="shipmentForm.trackNo" placeholder="输入运单号（如：SF1234567890）" />
        </el-form-item>
        <el-form-item label="发货备注">
          <el-input v-model="shipmentForm.remark" type="textarea" placeholder="填写发货备注（选填）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createShipmentVisible = false">取消</el-button>
        <el-button type="primary" @click="handleCreateShipment">确认发货</el-button>
      </template>
    </el-dialog>

    <!-- 修改运单号弹窗 -->
    <el-dialog title="修改运单号" v-model="editShipmentVisible" width="500px">
      <el-form label-width="100px">
        <el-form-item label="原运单号">
          <div class="old-trackno">{{ currentShipment ? currentShipment.trackNo : '' }}</div>
        </el-form-item>
        <el-form-item label="新运单号" required>
          <el-input v-model="editForm.trackNo" placeholder="输入新的运单号" />
        </el-form-item>
        <el-form-item label="修改原因" required>
          <el-input v-model="editForm.reason" type="textarea" placeholder="请输入修改原因（必填）" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editShipmentVisible = false">取消</el-button>
        <el-button type="primary" @click="handleUpdateTrackNo">保存</el-button>
      </template>
    </el-dialog>

    <!-- 管理员发起退款弹窗 -->
    <el-dialog title="管理员发起退款" v-model="initiateRefundVisible" width="600px">
      <el-form label-width="120px">
        <el-form-item label="订单号">
          <el-input v-model="refundForm.orderNo" disabled />
        </el-form-item>
        <el-form-item label="退款金额">
          <el-input v-model="refundForm.refundAmount" disabled>
            <template #append>元</template>
          </el-input>
        </el-form-item>
        <el-form-item label="退款原因" required>
          <el-input v-model="refundForm.refundReason" type="textarea"
                    placeholder="请输入退款原因"
                    :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="initiateRefundVisible = false">取消</el-button>
        <el-button type="primary" @click="handleInitiateRefund">确认退款</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  listOrders, getOrderDetail, getDashboardStats,
  listRefunds, approveRefund as approveRefundApi,
  rejectRefund as rejectRefundApi, syncRefund as syncRefundApi,
  initiateRefund as initiateRefundApi,
  listShipmentsByOrder, createShipment, updateShipmentTrackNo,
  deleteShipment as deleteShipmentApi, getLogisticsLogs,
} from '@/api/mall';
import { ElMessageBox } from 'element-plus';

export default {
  name: 'OrderManagement',
  data() {
    return {
      searchForm: { orderNo: '', orderStatus: null },
      orderList: [],
      loading: false,
      page: 1, size: 20, total: 0,
      stats: {},
      refundList: [], refundLoading: false, refundStatus: 'REVIEWABLE',
      refundPage: 1, refundSize: 10, refundTotal: 0,
      detailVisible: false, detail: null,
      activeTab: 'basic',
      shipments: [],
      logisticsLogs: [],
      createShipmentVisible: false,
      editShipmentVisible: false,
      shipmentForm: { trackNo: '', remark: '' },
      editForm: { trackNo: '', reason: '' },
      currentShipment: null,
      shippingOrder: null,
      initiateRefundVisible: false,
      refundForm: { orderNo: '', refundAmount: '', refundReason: '' },
    };
  },
  mounted() {
    this.loadStats();
    this.loadOrders();
    this.loadRefunds();
  },
  methods: {
    freightText(type) {
      if (type === 'COLLECT') return '到付';
      return '包邮';
    },
    async loadStats() {
      try { const res = await getDashboardStats(); this.stats = res.data || {}; } catch (e) {}
    },
   handlePageChange(page) {
     this.page = page;
     this.loadOrders();
   },
   handleRefundPageChange(page) {
     this.refundPage = page;
     this.loadRefunds();
   },
   handleRefundFilter() {
     this.refundPage = 1;
     this.loadRefunds();
   },
   async loadRefunds() {
     this.refundLoading = true;
     try {
       const res = await listRefunds({
         status: this.refundStatus || undefined,
         page: this.refundPage,
         size: this.refundSize,
       });
       const pageData = res.data || res;
       this.refundList = pageData.records || [];
       this.refundTotal = pageData.total || 0;
     } catch (e) { console.error(e); }
     finally { this.refundLoading = false; }
   },
   async loadOrders() {
     this.loading = true;
     try {
       const res = await listOrders({ page: this.page, size: this.size, ...this.searchForm });
       const pageData = res.data || res;
       this.orderList = pageData.records || [];
       this.total = pageData.total || 0;
     } catch (e) { console.error(e); }
     finally { this.loading = false; }
   },
   handleSizeChange(size) {
     this.size = size;
     this.page = 1;
     this.loadOrders();
   },
   statusText(s) {
      const m = { 0: '待支付', 1: '已支付', 2: '已发货', 3: '已完成', 4: '已取消', 5: '退款中', 6: '已退款' };
      return m[s] || '未知';
    },
   statusTag(s) {
      const m = { 0: 'warning', 1: 'primary', 2: 'success', 3: 'info', 4: 'info', 5: 'warning', 6: 'success' };
      return m[s] || '';
    },
    refundStatusText(status) {
      const map = {
        PENDING: '待审核', REQUESTING: '提交确认中', PROCESSING: '微信处理中',
        REQUEST_FAILED: '提交失败可重试', SUCCESS: '退款成功', CLOSED: '微信已关闭',
        ABNORMAL: '退款异常', REJECTED: '已拒绝', CANCELLED: '用户已撤销',
      };
      return map[status] || status;
    },
    refundStatusTag(status) {
      const map = {
        PENDING: 'warning', REQUESTING: 'warning', PROCESSING: 'warning',
        REQUEST_FAILED: 'danger', SUCCESS: 'success', CLOSED: 'danger',
        ABNORMAL: 'danger', REJECTED: 'info', CANCELLED: 'info',
      };
      return map[status] || 'info';
    },
    canApproveRefund(row) {
      return ['PENDING', 'REQUEST_FAILED', 'CLOSED'].includes(row.status);
    },
    canRejectRefund(row) {
      return ['PENDING', 'REQUEST_FAILED', 'CLOSED'].includes(row.status);
    },
    canSyncRefund(row) {
      return !!row.outRefundNo && ['REQUESTING', 'PROCESSING', 'ABNORMAL'].includes(row.status);
    },
    async handleApproveRefund(row) {
      const action = row.status === 'CLOSED' ? '使用新的商户退款单号重新退款' : '批准并提交微信退款';
      try {
        await ElMessageBox.confirm(`确定${action}？订单号：${row.orderNo}，金额：¥${row.refundAmount}`, '资金操作确认', {
          type: 'warning', confirmButtonText: '确认', cancelButtonText: '取消',
        });
        await approveRefundApi({ orderNo: row.orderNo, remark: action });
        this.$message.success('退款请求已提交，请以后续微信状态为准');
        this.loadRefunds();
        this.loadOrders();
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') this.$message.error(e?.message || '退款提交失败');
      }
    },
    async handleRejectRefund(row) {
      try {
        const result = await ElMessageBox.prompt('请输入拒绝或关闭退款申请的原因', '退款审核', {
          inputValidator: value => !!value?.trim() || '必须填写原因',
          confirmButtonText: '确认', cancelButtonText: '取消',
        });
        await rejectRefundApi({ orderNo: row.orderNo, rejectReason: result.value.trim() });
        this.$message.success('退款申请已关闭');
        this.loadRefunds();
        this.loadOrders();
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') this.$message.error(e?.message || '操作失败');
      }
    },
    async handleSyncRefund(row) {
      try {
        await syncRefundApi({ orderNo: row.orderNo, outRefundNo: row.outRefundNo });
        this.$message.success('微信退款状态已同步');
        this.loadRefunds();
        this.loadOrders();
      } catch (e) { this.$message.error(e?.message || '同步失败'); }
    },
    async showDetail(row) {
      try {
        const res = await getOrderDetail(row.orderNo);
        this.detail = res.data;
        this.detailVisible = true;
        this.activeTab = 'basic';
        await this.loadShipments(row.orderNo);
        await this.loadLogisticsLogs(row.orderNo);
      } catch (e) { this.$message.error('获取详情失败'); }
    },
    async loadShipments(orderNo) {
      try {
        const res = await listShipmentsByOrder(orderNo);
        this.shipments = res.data || [];
      } catch (e) { this.shipments = []; }
    },
    async loadLogisticsLogs(orderNo) {
      try {
        const res = await getLogisticsLogs(orderNo);
        this.logisticsLogs = res.data || [];
      } catch (e) { this.logisticsLogs = []; }
    },
    openCreateShipment(row) {
      this.shippingOrder = row;
      this.shipmentForm = { trackNo: '', remark: '' };
      this.createShipmentVisible = true;
    },
    async handleCreateShipment() {
      if (!this.shipmentForm.trackNo) {
        this.$message.warning('请输入运单号');
        return;
      }
      try {
        await createShipment({
          orderNo: this.shippingOrder.orderNo,
          trackNo: this.shipmentForm.trackNo,
          remark: this.shipmentForm.remark,
        });
        this.$message.success('发货成功');
        this.createShipmentVisible = false;
        this.loadOrders();
        if (this.detailVisible) {
          this.loadShipments(this.shippingOrder.orderNo);
          this.loadLogisticsLogs(this.shippingOrder.orderNo);
        }
      } catch (e) { this.$message.error(e?.message || '发货失败'); }
    },
    openEditShipment(shipment) {
      this.currentShipment = shipment;
      this.editForm = { trackNo: '', reason: '' };
      this.editShipmentVisible = true;
    },
    async handleUpdateTrackNo() {
      if (!this.editForm.trackNo) {
        this.$message.warning('请输入新运单号');
        return;
      }
      if (!this.editForm.reason) {
        this.$message.warning('请输入修改原因');
        return;
      }
      try {
        await updateShipmentTrackNo({
          shipmentId: this.currentShipment.id,
          trackNo: this.editForm.trackNo,
          reason: this.editForm.reason,
        });
        this.$message.success('修改成功');
        this.editShipmentVisible = false;
        this.loadShipments(this.detail.orderNo);
        this.loadLogisticsLogs(this.detail.orderNo);
      } catch (e) { this.$message.error(e?.message || '修改失败'); }
    },
    async deleteShipment(shipmentId) {
      try {
        await ElMessageBox.confirm('确定删除该发货记录吗？', '提示', { type: 'warning' });
        await deleteShipmentApi(shipmentId);
        this.$message.success('删除成功');
        this.loadShipments(this.detail.orderNo);
        this.loadLogisticsLogs(this.detail.orderNo);
      } catch (e) {
        if (e !== 'cancel' && e !== 'close') this.$message.error(e?.message || '删除失败');
      }
    },
    shipmentStatusTag(status) {
      const map = { 0: 'info', 1: 'success', 2: 'primary', 3: 'success', 4: 'danger' };
      return map[status] || '';
    },
    shipmentStatusText(status) {
      const map = { 0: '待发货', 1: '已发货', 2: '运输中', 3: '已签收', 4: '异常' };
      return map[status] || '';
    },
    changeTypeText(type) {
      const map = { 1: '新增发货', 2: '修改运单号', 3: '删除发货' };
      return map[type] || '';
    },
    async openInitiateRefund(row) {
      // 已发货/已完成订单：退款前二次确认
      if (row.orderStatus === 2 || row.orderStatus === 3) {
        try {
          await ElMessageBox.confirm('该订单已发货，是否确认退款？', '退款确认', {
            type: 'warning', confirmButtonText: '确认退款', cancelButtonText: '取消',
          });
        } catch (e) {
          return;
        }
      }
      this.refundForm.orderNo = row.orderNo;
      this.refundForm.refundAmount = row.payableAmount;
      this.refundForm.refundReason = '';
      this.initiateRefundVisible = true;
    },
    async handleInitiateRefund() {
      if (!this.refundForm.refundReason?.trim()) {
        this.$message.warning('请输入退款原因');
        return;
      }
      try {
        await initiateRefundApi({
          orderNo: this.refundForm.orderNo,
          refundReason: this.refundForm.refundReason,
        });
        this.$message.success('退款已提交，请以后续微信状态为准');
        this.initiateRefundVisible = false;
        this.loadRefunds();
        this.loadOrders();
      } catch (e) {
        this.$message.error(e?.message || '发起退款失败');
      }
    },
  },
};
</script>

<style scoped>
.order-management { padding: 0; }
.search-bar { background: #fff; padding: 16px; border-radius: 4px; margin-bottom: 16px; }
.refund-panel { margin-bottom: 16px; }
.refund-header { display:flex; align-items:center; justify-content:space-between; }
.stat-card { display:flex; flex-direction:column; align-items:center; }
.stat-num { font-size:28px; font-weight:bold; color:#409EFF; }
.stat-label { font-size:13px; color:#999; margin-top:4px; }
.pagination { margin-top: 16px; display: flex; justify-content: flex-end; }

.shipment-section {
  .empty-hint {
    text-align: center;
    color: #999;
    padding: 40px 0;
  }
  .shipment-card {
    border: 1px solid #EBEEF5;
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 16px;
    .shipment-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      padding-bottom: 12px;
      border-bottom: 1px solid #EBEEF5;
    }
    .shipment-body {
      p { margin: 8px 0; font-size: 14px; color: #606266; }
    }
    .shipment-items {
      margin: 16px 0;
      h4 { margin-bottom: 8px; font-size: 14px; }
    }
    .shipment-actions {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #EBEEF5;
      display: flex;
      gap: 8px;
    }
  }
}
.old-trackno {
  padding: 8px 12px;
  background: #F5F7FA;
  border-radius: 4px;
  color: #909399;
}
</style>
